/**
 * Toast Notification Component
 *
 * Simple toast notification system for displaying success, error, and info messages.
 * Automatically dismisses after a configurable duration.
 *
 * @module components/ui/Toast
 */

import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '@/hooks/useTheme';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

export interface ToastProps {
  /** The message to display */
  message: string;
  /** Type of toast (success, error, info, warning) */
  type?: ToastType;
  /** Duration in milliseconds before auto-dismiss (0 = no auto-dismiss) */
  duration?: number;
  /** Callback when toast is dismissed */
  onClose: () => void;
  /** Whether the toast is visible */
  isOpen: boolean;
}

/**
 * Toast Component
 */
export const Toast: React.FC<ToastProps> = ({
  message,
  type = 'info',
  duration = 3000,
  onClose,
  isOpen,
}) => {
  const { isDark } = useTheme();

  // Auto-dismiss after duration
  useEffect(() => {
    if (isOpen && duration > 0) {
      const timer = setTimeout(() => {
        onClose();
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [isOpen, duration, onClose]);

  if (!isOpen) return null;

  // Color schemes for different toast types
  const getColorScheme = () => {
    switch (type) {
      case 'success':
        return {
          bg: isDark ? '#065f46' : '#d1fae5',
          border: isDark ? '#10b981' : '#059669',
          text: isDark ? '#d1fae5' : '#065f46',
          icon: '✓',
        };
      case 'error':
        return {
          bg: isDark ? '#7f1d1d' : '#fee2e2',
          border: isDark ? '#ef4444' : '#dc2626',
          text: isDark ? '#fecaca' : '#7f1d1d',
          icon: '✕',
        };
      case 'warning':
        return {
          bg: isDark ? '#78350f' : '#fef3c7',
          border: isDark ? '#f59e0b' : '#d97706',
          text: isDark ? '#fde68a' : '#78350f',
          icon: '⚠',
        };
      case 'info':
      default:
        return {
          bg: isDark ? '#1e3a8a' : '#dbeafe',
          border: isDark ? '#3b82f6' : '#2563eb',
          text: isDark ? '#bfdbfe' : '#1e3a8a',
          icon: 'ℹ',
        };
    }
  };

  const colors = getColorScheme();

  return createPortal(
    <div
      role="alert"
      aria-live="polite"
      style={{
        position: 'fixed',
        top: '20px',
        right: '20px',
        zIndex: 99999,
        minWidth: '300px',
        maxWidth: '500px',
        backgroundColor: colors.bg,
        border: `2px solid ${colors.border}`,
        borderRadius: '8px',
        padding: '16px',
        boxShadow: isDark
          ? '0 10px 15px -3px rgba(0, 0, 0, 0.4)'
          : '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        animation: 'slideIn 0.3s ease-out',
      }}
    >
      {/* Icon */}
      <div
        style={{
          fontSize: '20px',
          fontWeight: 'bold',
          color: colors.text,
          flexShrink: 0,
        }}
      >
        {colors.icon}
      </div>

      {/* Message */}
      <div
        style={{
          flex: 1,
          fontSize: '14px',
          color: colors.text,
          lineHeight: '1.5',
        }}
      >
        {message}
      </div>

      {/* Close button */}
      <button
        onClick={onClose}
        aria-label="Close notification"
        style={{
          padding: '4px',
          border: 'none',
          background: 'transparent',
          color: colors.text,
          cursor: 'pointer',
          fontSize: '18px',
          lineHeight: '1',
          flexShrink: 0,
        }}
      >
        ✕
      </button>

      {/* Animation styles */}
      <style>{`
        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>
    </div>,
    document.body
  );
};

export default Toast;